import type { ReactElement, ReactNode } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { BrandMark } from '../brand/index.js';
import { SeatBadge, type Seat } from '../system/index.js';

export interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

export function Topbar({
  nav = [],
  actions,
  seat,
}: {
  nav?: NavItem[];
  actions?: ReactNode;
  /** Whose half of the product this is. One word beside the mark. */
  seat?: Seat;
}): ReactElement {
  return (
    <header className="topbar">
      {/* The real mark, not a coloured square. This is the most-seen piece of
          brand in the product — it is on every page — and it was the one place
          the motif did not appear. */}
      <Link to="/" className="topbar__brand">
        <BrandMark size={22} />
        Equipment Rental
      </Link>
      {seat === undefined ? null : <SeatBadge seat={seat} />}
      <nav className="topbar__nav" aria-label="Primary">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            {...(item.end === undefined ? {} : { end: item.end })}
            className="topbar__link"
          >
            {item.label}
          </NavLink>
        ))}
      </nav>
      <span className="topbar__spacer" />
      {actions}
    </header>
  );
}

export interface SidebarGroup {
  label: string;
  items: NavItem[];
}

/** Persistent from lg; hidden below, where the tab bar or topbar nav carries. */
export function Sidebar({
  groups,
  label,
}: {
  groups: SidebarGroup[];
  label: string;
}): ReactElement {
  return (
    <nav className="sidebar" aria-label={label}>
      {groups.map((group) => (
        <div key={group.label}>
          <p className="sidebar__group">{group.label}</p>
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              {...(item.end === undefined ? {} : { end: item.end })}
              className="sidebar__link"
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

export function MobileTabBar({ items }: { items: NavItem[] }): ReactElement {
  return (
    <nav className="tabbar" aria-label="Sections">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          {...(item.end === undefined ? {} : { end: item.end })}
          className="tabbar__link"
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}): ReactElement {
  return (
    <div className="page-header">
      <div>
        <h1 className="page-header__title">{title}</h1>
        {description === undefined ? null : (
          <p className="page-header__description">{description}</p>
        )}
      </div>
      {actions}
    </div>
  );
}

export interface Crumb {
  label: string;
  to?: string;
}

/** Only from depth 3 — shallower pages read better without them. */
export function Breadcrumbs({ items }: { items: Crumb[] }): ReactElement {
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {items.map((crumb, index) => (
        <span key={`${crumb.label}-${index}`}>
          {crumb.to === undefined ? (
            <span aria-current="page">{crumb.label}</span>
          ) : (
            <Link to={crumb.to}>{crumb.label}</Link>
          )}
          {index < items.length - 1 ? <span aria-hidden="true"> / </span> : null}
        </span>
      ))}
    </nav>
  );
}

/**
 * The unauthenticated header — ARCH §3.
 *
 * Distinct from `Topbar` only in what it carries, not how it is built: the
 * public pages have no sidebar, so the header is the ONLY navigation a visitor
 * has, and it must also state the two things a marketplace visitor arrives
 * wanting to know — that machines can be browsed without an account, and that
 * owners can list. It composes `Topbar` rather than re-implementing it, so the
 * brand, the sticky behaviour and the focus styles cannot drift apart.
 */
export function PublicHeader({ actions }: { actions?: ReactNode }): ReactElement {
  return (
    <Topbar
      nav={[
        { to: '/equipment', label: 'Browse machines' },
        { to: '/owner', label: 'List your equipment' },
      ]}
      {...(actions === undefined ? {} : { actions })}
    />
  );
}
